import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { Logo } from "../../components/site/Logo";

export const AuthCard = ({ title, text, children, footer }) => (
  <div className="relative grid min-h-screen place-items-center overflow-hidden px-5 py-12 grain">
    <div className="grid-lines absolute inset-0 opacity-50" />
    <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[110px]" />
    <div className="rise relative w-full max-w-md">
      <div className="mb-8 flex justify-center"><Logo /></div>
      <div className="card-rx p-8 shadow-2xl">
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        {text && <p className="mt-1.5 text-sm text-slate-400">{text}</p>}
        <div className="mt-7">{children}</div>
      </div>
      {footer && <p className="mt-6 text-center text-sm text-slate-400">{footer}</p>}
    </div>
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
    {children}
  </label>
);

export const PasswordInput = ({ testId, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...props} type={show ? "text" : "password"} className="input-rx pr-11" data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" aria-label="Toggle password" data-testid={`${testId}-toggle`}>
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
};

export const FormError = ({ msg, testId = "form-error" }) =>
  msg ? <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-300" data-testid={testId}>{msg}</p> : null;

const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") ? n : null);

export function Login() {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(phone, password);
      nav(safeNext(params.get("next")) || (u.role === "admin" ? "/admin" : "/dashboard"), { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <AuthCard title="Welcome back" text="Log in with your phone number." footer={<>New to Raphanex? <Link to="/register" className="font-semibold text-emerald-300" data-testid="login-to-register">Create an account</Link></>}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="Phone number"><input required value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="024 123 4567" className="input-rx" data-testid="login-phone-input" /></Field>
        <Field label="Password"><PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" testId="login-password-input" /></Field>
        <FormError msg={err} testId="login-error" />
        <button disabled={busy} className="btn-primary w-full" data-testid="login-submit-btn">{busy ? "Signing in…" : "Log in"}</button>
        <Link to="/forgot-password" className="block text-center text-sm text-slate-400 hover:text-white" data-testid="forgot-password-link">Forgot password?</Link>
      </form>
    </AuthCard>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", phone: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Passwords do not match");
    setErr(""); setBusy(true);
    try {
      await register({ full_name: f.full_name, phone: f.phone, password: f.password });
      nav("/dashboard", { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <AuthCard title="Create your account" text="Free, quick and secure." footer={<>Already have an account? <Link to="/login" className="font-semibold text-emerald-300" data-testid="register-to-login">Log in</Link></>}>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <Field label="Full name"><input required minLength={2} value={f.full_name} onChange={set("full_name")} placeholder="Ama Mensah" className="input-rx" data-testid="register-name-input" /></Field>
        <Field label="Phone number"><input required value={f.phone} onChange={set("phone")} inputMode="tel" placeholder="024 123 4567" className="input-rx" data-testid="register-phone-input" /></Field>
        <Field label="Password"><PasswordInput required minLength={8} value={f.password} onChange={set("password")} placeholder="At least 8 characters, letters & numbers" testId="register-password-input" /></Field>
        <Field label="Confirm password"><PasswordInput required value={f.confirm} onChange={set("confirm")} placeholder="Repeat password" testId="register-confirm-input" /></Field>
        <FormError msg={err} testId="register-error" />
        <button disabled={busy} className="btn-primary w-full" data-testid="register-submit-btn">{busy ? "Creating account…" : "Create account"}</button>
        <p className="text-center text-xs text-slate-500">By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
      </form>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { phone }); setMsg(data.message); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthCard title="Reset your password" text="Enter the phone number on your account." footer={<Link to="/login" className="font-semibold text-emerald-300">Back to login</Link>}>
      {msg ? <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200" data-testid="forgot-success">{msg}</p> : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <Field label="Phone number"><input required value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" className="input-rx" data-testid="forgot-phone-input" /></Field>
          <FormError msg={err} />
          <button className="btn-primary w-full" data-testid="forgot-submit-btn">Request reset</button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", new_password: pw }); nav("/login"); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthCard title="Choose a new password">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <Field label="New password"><PasswordInput required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} testId="reset-password-input" /></Field>
        <FormError msg={err} />
        <button className="btn-primary w-full" data-testid="reset-submit-btn">Update password</button>
      </form>
    </AuthCard>
  );
}
